import React, { useState } from 'react';
import { useLayers } from '@agissc/ktag-map';
import { useTranslation } from 'react-i18next';
import useAppState from '../../../../AppStore';
import TopicLayerAttributeSearchQueryFields from './TopicLayerAttributeSearchQueryFields';
import TopicLayerAttributeSearchResults from './TopicLayerAttributeSearchResults';
import AttributeSearchPlaceholder from './AttributeSearchPlaceholder';
import { useHasAttributeSearch } from './useHasAttributeearch';
import ZoomToAttributeSearch from './ZoomToAttributeSearch';
import useDisplaySize from '../../../../useDisplaySize';
import Scrollable from '../../../../components/scrollable/Scrollable';

interface TopicLayerAttributeSearchDisplayProps {
  topicId: string;
}

const TopicLayerAttributeSearchDisplay = ({
  topicId,
}: TopicLayerAttributeSearchDisplayProps) => {
  const { t } = useTranslation('agisviewer');
  const attributeSearch = useAppState((state) => state.attributeSearch);
  const { isSmallScreen } = useDisplaySize();

  const [shouldZoom, setShouldZoom] = useState(false);
  const onSearchRequest = () => setShouldZoom(true);
  const onZoomCompleted = () => setShouldZoom(false);

  const { isLoading, hasAttributeSearch } = useHasAttributeSearch(topicId);

  const layersQuery = useLayers();

  if (isLoading || layersQuery.isLoading) return <AttributeSearchPlaceholder />;

  if (!hasAttributeSearch)
    return <div style={{ padding: '2rem' }}>{t('attribute_search_unavailable')}</div>;

  const topicLayers = layersQuery.data ?? {};
  const topicLayer = topicLayers[topicId];
  const showSearchResults = attributeSearch[topicId]?.query ?? null;

  return (
    <Scrollable noScroll={!isSmallScreen} additionalClassName="attribute-search">
      <TopicLayerAttributeSearchQueryFields
        topicLayer={topicLayer}
        showInitialMessage={!showSearchResults}
        onSearchRequest={onSearchRequest}
      />
      {showSearchResults && <TopicLayerAttributeSearchResults topicId={topicId} />}
      {shouldZoom && (
        <ZoomToAttributeSearch topicId={topicId} callback={onZoomCompleted} />
      )}
    </Scrollable>
  );
};

const TopicLayerAttributeSearch = () => {
  const currentTopicLayerId = useAppState((state) => state.currentTopicLayerId);

  return currentTopicLayerId ? (
    <TopicLayerAttributeSearchDisplay topicId={currentTopicLayerId} />
  ) : (
    <div>Keine aktive Ebene</div>
  );
};

export default TopicLayerAttributeSearch;
